import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Host,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { ControlContainer, FormArray, FormBuilder, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { Router } from '@angular/router';
import { AuthService } from '@klickdata/core/auth';
import { ConfigService } from '@klickdata/core/config';
import { Customer, CustomerService } from '@klickdata/core/customer';
import { FormHelper } from '@klickdata/core/form';
import { Media } from '@klickdata/core/media/src/media.model';
import { MediaService } from '@klickdata/core/media/src/media.service';
import { S3MediaService } from '@klickdata/core/media/src/s3-media.service';
import { MessageSavedComponent } from '@klickdata/core/message';
import { MessageErrorComponent } from '@klickdata/core/message/src/message-error/message-error.component';
import { MessageService } from '@klickdata/core/message/src/message.service';
import { MobileService, SideNaveActionsTypes, SideNaveDataTypes } from '@klickdata/core/mobile';
import { AppScope, Resource, ResourceService, ResourceTypes } from '@klickdata/core/resource';
import { ResourceItem, ResourceItemData } from '@klickdata/core/resource-item';
import { Currency, Educator, OccasionDate } from '@klickdata/core/resource/src/resource.model';
import { ResourceStaffRoles } from '@klickdata/core/resource/src/types.enum';
import { User } from '@klickdata/core/user';
import { Utils } from '@klickdata/core/util';
import { DialogDisplayImgComponent } from 'apps/klickdata/src/app/shared/dialog/dialog-display-img/dialog-display-img.component';
import * as moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map,
    share,
    startWith,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs/operators';
import { CourseManagerCoreComponent } from '../course-manager-core.component';
import { CourseManagerDiplomaLogosComponent } from './course-manager-diploma-logos/course-manager-diploma-logos.component';
import { CustomValidatorService } from '@klickdata/core/form/src/validators/custom-validator.service';
import { ResourceFormPracticeControlComponent } from '../../resource-form-practice-control/resource-form-practice-control.component';
import { CustomField } from '@klickdata/core/custom-field';
import { VideoLinkControlComponent } from '../../video-link-control/video-link-control.component';
@Component({
    selector: 'app-course-manager-core-secodary-controls',
    templateUrl: './course-manager-core-secodary-controls.component.html',
    styleUrls: ['./course-manager-core-secodary-controls.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class CourseManagerCoreSecodaryControlsComponent implements OnInit, OnDestroy, OnChanges {
    @Input() resource: Resource;
    @Input() customer: Customer;
    @Input() customFields$: Observable<CustomField[]>;
    public itemsHaveTests: boolean;
    public mandatoryTestResourceItems: Observable<ResourceItemData[]>;
    @ViewChild('uploadInput') uploadInput: ElementRef;
    @ViewChild('uploadPublisherLogoInput') uploadPublisherLogoInput: ElementRef;
    @ViewChild('authorsAddingTemplate') authorsAddingTemplate: TemplateRef<any>;
    @ViewChild('agendaExpansionPanel') agendaExpansionPanel: MatExpansionPanel;
    @ViewChild('occasionExpansionPanel') occasionExpansionPanel: MatExpansionPanel;
    @ViewChild('practiceControlComponent') practiceControlComponent: ResourceFormPracticeControlComponent;
    @ViewChild(CourseManagerDiplomaLogosComponent) diplomaLogos: CourseManagerDiplomaLogosComponent;
    @ViewChild(VideoLinkControlComponent) videoLinkControl: VideoLinkControlComponent;
    public resourceForm: FormGroup;
    public defaultResourceSummaryCount: number;
    public defaultResourceOnlineEventId: number;
    public maxResourceInstructors: number;
    public defaultCourseGoalCount: number;
    public isArticleCodeValid: Observable<boolean>;
    ResourceStaffRoles = ResourceStaffRoles;
    public currencies: Currency[];
    public saving: boolean;
    public playTimeOptions = {
        zeroValue: null,
        showPreview: false,
        showYears: false,
        showMonths: false,
        showDays: false,
        showWeeks: false,
    };
    public durationOptions = {
        zeroValue: null,
        showPreview: false,
        showYears: false,
        showSeconds: false,
    };
    public instructorUploading: BehaviorSubject<boolean> = new BehaviorSubject(false);
    AppScope = AppScope;
    ResourceTypes = ResourceTypes;
    public destroy: Subject<boolean> = new Subject<boolean>();
    public user$: Observable<User>;
    public progress$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    public publisherProgress$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
    public unlimited_seats = new FormControl(true);
    public authorsLists: Educator[] = [];
    public toggleAddAuthor: boolean = false;
    public toggleAddCustomAuthor: boolean = false;
    public addAuthorName = new FormControl('');
    Utils = Utils;

    constructor(
        @Host() protected host: CourseManagerCoreComponent,
        protected parentFormDirective: FormGroupDirective,
        protected configService: ConfigService,
        protected cdRef: ChangeDetectorRef,
        protected dialog: MatDialog,
        protected fb: FormBuilder,
        protected resourceService: ResourceService,
        protected mobileService: MobileService,
        protected router: Router,
        protected auth: AuthService,
        protected s3MediaService: S3MediaService,
        protected mediaService: MediaService,
        protected customerService: CustomerService,
        protected messageService: MessageService,
        protected validatorService: CustomValidatorService
    ) {
        this.defaultResourceSummaryCount = this.configService.config.defaultResourceSummaryCharCount;
        this.defaultCourseGoalCount = this.configService.config.defaultResourceGoalCharCount;
        this.defaultResourceOnlineEventId = this.configService.config.defaultResourceOnlineEventId;
        this.maxResourceInstructors = this.configService.config.maxResourceInstructors;
        this.resourceForm = parentFormDirective.form;
        this.buildForm(this.resourceForm);
        this.currencies = Utils.getAllCurrencies();
    }

    ngOnInit(): void {
        this.unlimited_seats.valueChanges.pipe(takeUntil(this.destroy)).subscribe((unlimited) => {
            if (unlimited) {
                this.resourceForm.get('total_seats').setValue(0);
                this.resourceForm.get('total_seats').disable();
            } else {
                this.resourceForm.get('total_seats').enable();
            }
            this.resourceForm.get('total_seats').markAsDirty();
        });

        this.mandatoryTestResourceItems = this.resourceForm.get('items').valueChanges.pipe(
            takeUntil(this.destroy),
            filter((items) => !!items),
            map((items) => items.filter((item) => item.mandatory && item.item_type_value === 'test')),
            tap((items) => {
                if (items.length !== 0) {
                    this.itemsHaveTests = true;
                    if (!this.resourceForm.get('examination')?.value.final_test_id) {
                        this.resourceForm
                            .get('examination')
                            .patchValue({ final_test_id: items[items.length - 1].child_resource_id });
                    }
                }
                this.cdRef.markForCheck();
            })
        );

        this.user$ = this.auth.getUser().pipe(first());
        this.resourceForm.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
            this.cdRef.detectChanges();
            this.cdRef.markForCheck();
        });
        this.resourceForm
            .get('staff.manager')
            .valueChanges.pipe(
                takeUntil(this.destroy),
                startWith(this.resourceForm.value.staff.manager),
                switchMap((managers) => this.resourceService.getResourceEducators({ ids: managers }))
            )
            .subscribe((value) => {
                this.authorsLists = value;
                this.cdRef.markForCheck();
            });
    }
    removeAuthor(author?: Educator) {
        const index = this.authorsLists.findIndex((a) => a.id === author.id);
        if (index !== -1) {
            this.authorsLists.splice(index, 1);
        }
        this.cdRef.markForCheck();
    }
    addPublisher() {}
    onUpdateEducators(educators: Educator[]) {
        this.authorsLists = [
            ...educators.filter((edu) => !this.authorsLists.some((author) => author.id === edu.id)),
            ...this.authorsLists,
        ];
        this.cdRef.markForCheck();
    }
    addAuthor() {}
    addExistingAuthor() {}
    onUploadComplete(ev: any) {}
    ngOnChanges(changes: SimpleChanges): void {
        if (this.resource) {
            this.updateForm();
        }
        if (
            !!this.customer?.medias?.publisher?.length &&
            !this.resourceForm.value.collaboration?.view?.metadata?.publisher_logo
        ) {
            this.resourceForm
                .get('collaboration.view.metadata.publisher_logo')
                .patchValue(this.customer.medias.publisher[0]);
            this.cdRef.markForCheck();
        }
    }
    public onTabSelectionChange(activeIndex: number) {
        this.agendaExpansionPanel?.close();
        this.occasionExpansionPanel?.close();
    }
    public toggleLoading(ev: boolean) {
        ev ? this.instructorUploading.next(true) : this.instructorUploading.next(false);
    }
    public showImgModal(id: number) {
        this.dialog.open(DialogDisplayImgComponent, {
            maxWidth: '70%',
            maxHeight: '50vh',
            data: id,
        });
    }
    private buildForm(resForm: FormGroup) {
        resForm.addControl('goal', new FormControl(''));
        resForm.addControl('time_limit', new FormControl(''));
        resForm.addControl('duration', new FormControl(''));
        resForm.addControl('other_info', new FormControl(''));
        resForm.addControl('price', new FormControl(''));
        resForm.addControl('promo_code', new FormControl(''));
        resForm.addControl('finish_msg', new FormControl());
        resForm.addControl('article_code', new FormControl(''));
        resForm.addControl('sorting_code', new FormControl(0));
        resForm.addControl('subtitles', new FormControl(null));
        resForm.addControl('show_publisher_logo', new FormControl(false));
        resForm.addControl('total_seats', new FormControl({ value: 0, disabled: true }));
        resForm.addControl('audience', new FormControl(''));
        resForm.addControl('pre_skills', new FormControl(''));
        resForm.addControl('agenda', new FormControl(''));
        // resForm.addControl('episode', new FormControl(''));
        resForm.addControl('extra_seats', new FormControl());
        resForm.addControl('copyrights', new FormControl(''));
        resForm.addControl('priority', new FormControl(''));
        resForm.addControl(
            'examination',
            this.fb.group({
                type: ['Participation'],
                grade_system_id: [],
                final_test_id: [],
                diploma_level: [],
            })
        );
    }

    private updateForm() {
        if (this.resource.duration) {
            this.resourceForm.patchValue({
                duration: moment.duration({ s: <number>this.resource.duration }).toISOString(),
            });
            FormHelper.resetForm(this.resourceForm.get('duration'));
        }
        if (this.resource.time_limit) {
            this.resourceForm.patchValue({
                time_limit: moment.duration({ s: <number>this.resource.time_limit }).toISOString(),
            });
            FormHelper.resetForm(this.resourceForm.get('time_limit'));
        }
        if (!!this.resource.total_seats) {
            this.unlimited_seats.setValue(false, { emitEvent: false });
            this.resourceForm.get('total_seats').enable();
        }
    }

    get testItems(): ResourceItem[] {
        return (<ResourceItem[]>this.resourceForm.value.items).filter(
            (item) => item.item_type_value == 'test' && !!item.id
        );
    }
    public selectPublisherLogo(customer: Customer) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.SELECT_PUBLISHER_LOGO,
            data: {
                publisherLogos: customer.medias.publisher,
            },
        });
        this.mobileService
            .getSideNavResponseData()
            .pipe(filter((data) => data.type == 'selectPublisherLogo'))
            .subscribe((data) => {
                if (data.uploadLogo) {
                    this.uploadPublisherLogoInput.nativeElement.click();
                } else {
                    this.resourceForm.get('collaboration.view.metadata.publisher_logo').patchValue(data.publisherLogo);
                    this.resourceForm.get('collaboration.view.metadata.publisher_logo').markAsDirty();
                    this.cdRef.markForCheck();
                }
            });
    }

    getTestItemById(id: number): ResourceItem {
        return this.testItems.find((item) => item.id == id);
    }

    onTypeChange() {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.GENERAL_NOTIFIER,
            data: {
                icon: 'group_work',
                title: $localize`Can't change course type`,
                contentBody: $localize`If you want to create onsite, hybrid  or blended course. It'll be event. Event is the type that you can manage classroom, webinar, interview or conference in KLMS.`,
                positiveBtn: $localize`Create an event`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavAction()
            .pipe(filter((action) => action === SideNaveActionsTypes.POSITIVE))
            .subscribe((action) => {
                if (action) {
                    this.router.navigate(['/admin/content/events/create']);
                }
            });
    }
    public fileHandler(event: any, mediaControlName: string) {
        const file = event.target.files[0];
        if (!file) {
            return;
        }
        this.uploadFileToS3AndLink(file, this.progress$).subscribe(
            (media) => {
                this.saving = false;
                if (media && media.id) {
                    this.resourceForm.get('medias').get(mediaControlName).patchValue([media.id]);
                    FormHelper.markForm(this.resourceForm);
                    this.progress$.next(null);
                    this.cdRef.markForCheck();
                }
            },
            (err) => {} // TODO on error
        );
    }
    public onMediaUpdated(mediaId: any, control: string) {
        this.resourceForm.get(control).patchValue(Array.isArray(mediaId) ? mediaId : [mediaId]);
        FormHelper.markForm(this.resourceForm);
        this.cdRef.markForCheck();
    }
    public uploadFileToS3AndLink(file: File, progress$: BehaviorSubject<number>): Observable<Media> {
        return this.s3MediaService.uploadMediaToS3(file, progress$).pipe(
            takeUntil(this.destroy),
            switchMap((media) =>
                this.mediaService.uploadMediaByLink({
                    ...media.getData(),
                    scope_id: AppScope.RESOURCES,
                })
            )
        );
    }
    public uploadPublisherLogo(event: Event, customer: Customer) {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) return;
        this.uploadFileToS3AndLink(file, this.publisherProgress$)
            .pipe(
                switchMap((media) => {
                    const customerPublishers = !!customer.medias?.publisher?.length ? customer.medias?.publisher : [];
                    return this.customerService
                        .update({
                            id: customer.id,
                            medias: { publisher: [...customerPublishers, +media.id] },
                        })
                        .pipe(map(() => +media.id));
                })
            )
            .subscribe({
                next: (publisherLogoId) => {
                    this.resourceForm.get('collaboration.view.metadata.publisher_logo').patchValue(publisherLogoId);
                    this.resourceForm.get('collaboration.view.metadata.publisher_logo').markAsDirty();
                    this.publisherProgress$.next(0);
                    this.messageService.openMessage(MessageSavedComponent);
                    this.cdRef.markForCheck();
                },
                error: (err) => {
                    if (err?.error?.error) {
                        this.messageService.openMessage(MessageErrorComponent, err.error.error.messages.join('\n'));
                    }
                },
            });
    }
    editResourceAccessPermision(permType: string, value: any) {
        const sortingDir = value?.status && value?.exclude ? 'asc' : 'desc';

        this.host
            .editResourceAccessPermision(permType, sortingDir)
            .pipe(takeUntil(this.destroy))
            .subscribe(() => this.cdRef.markForCheck());
    }
    public updateResRecurring(customerId: number) {
        this.mobileService.updateSideNavSub({
            dataType: SideNaveDataTypes.UPDATE_RECURRING,
            data: {
                title: $localize`Update recurring`,
                resource: this.resource,
                type: 'updateRecurringResource',
                positiveBtn: $localize`Update`,
                negativeBtn: $localize`Cancel`,
            },
        });
        this.mobileService
            .getSideNavResponseData()
            .pipe(
                filter((data) => data.type == 'updateRecurringResource'),
                switchMap((data: any) =>
                    this.customerService
                        .update({
                            id: customerId,
                            recurring: {
                                resource_id: this.resource.id,
                                metadata: data.value,
                            },
                        })
                        .pipe(map(() => data.value))
                )
            )
            .subscribe((recurring) => {
                this.resource.recurring = recurring;
                this.cdRef.markForCheck();
            });
    }
    public getOtherInfoTooltipText() {
        return this.resourceForm.value.other_info
            ? this.resourceForm.value.metadata?.other_info
                ? $localize`Other information field has content that will be shown to the learners`
                : $localize`Other information field has content that will not be shown to the learners`
            : $localize`Other information field has no content`;
    }
    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
